"use client";
import { ASSET_PREFIX } from "@/config";
import Image from "next/image";
import { useState } from "react";

export type Person = {
  id: string;
  name: string;
  role: string;
  bio?: string;
  photo: string; // public path or remote URL
  accent?: string; // optional hex for gradient accent
};

export type LeadershipProps = {
  ceo: Person;
  executives: Person[]; // 2–6
  functionalHeads?: Person[]; // optional extra section
  variant?: "split-hero" | "card-grid"; // choose design
  className?: string;
};

/**
 * LeadershipSection - two polished Tailwind designs in one component.
 * Set `variant` to switch layouts:
 *  - "split-hero": Big CEO panel + executive row (inspired by first screenshot)
 *  - "card-grid": Clean card grid with hover lift (compact)
 */
export default function LeadershipSection({
  ceo,
  executives,
  functionalHeads = [],
  variant = "split-hero",
  className = "",
}: LeadershipProps) {
  const [mode, setMode] = useState<"split-hero" | "card-grid">(variant);
  return (
    <section className={`w-full ${className}`}>
      <div className="flex items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">
          Leadership
        </h2>
        <div className="inline-flex rounded-xl border bg-white/70 shadow-sm overflow-hidden">
          <button
            onClick={() => setMode("split-hero")}
            className={`px-3 py-1.5 text-[16px] leading-[22px] font-medium transition ${mode === "split-hero"
              ? "bg-gray-900 text-white"
              : "hover:bg-gray-100"
              }`}
            aria-pressed={mode === "split-hero"}>
            Split hero
          </button>
          <button
            onClick={() => setMode("card-grid")}
            className={`px-3 py-1.5 text-[16px] leading-[22px] font-medium transition ${mode === "card-grid"
              ? "bg-gray-900 text-white"
              : "hover:bg-gray-100"
              }`}
            aria-pressed={mode === "card-grid"}>
            Card grid
          </button>
        </div>
      </div>

      {mode === "split-hero" ? (
        <SplitHero
          ceo={ceo}
          executives={executives}
          functionalHeads={functionalHeads}
        />
      ) : (
        <CardGrid
          ceo={ceo}
          executives={executives}
          functionalHeads={functionalHeads}
        />
      )}
    </section>
  );
}

/* ----------------------------- VARIANT 1 ------------------------------ */
export function SplitHero({
  ceo,
  executives,
  functionalHeads = [],
}: LeadershipProps) {
  return (
    <div className="space-y-10">
      {/* CEO hero */}{" "}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px] md:gap-[60px] items-start">
        <div className="">
          <figure className="group relative rounded-3xl flex justify-center md:justify-end items-center overflow-hidden rounded-3xl shadow-xl w-fit mx-auto md:mx-0 md:ml-auto ">
            {/* <div
              className="absolute inset-0 opacity-70"
              style={{
                background: `linear-gradient(180deg, ${
                  ceo.accent ?? "#0B1220"
                } 0%, #F6D5C2 100%)`,
              }}
            /> */}
            <Image
              src={`${ASSET_PREFIX}${ceo.photo}`}
              alt={ceo.name}
              width={400}
              height={400}

              className="w-auto h-[320px] sm:h-[400px] lg:h-[500px] object-cover object-top group-hover:scale-[1.05] transition duration-300  "
              priority
            />
          </figure>
        </div>
        <div className="">
          <h3 className="text-[20px] leading-[28px]   md:text-[20px] md:leading-[28px]   xl:text-[22px] xl:leading-[30px]   2xl:text-[24px] 2xl:leading-[32px] text-[#000]  mb-[5px] font-[600] ">
            {ceo.name}
          </h3>
          <p className="mt-1 text-md font-medium text-[#6b5622]">{ceo.role}</p>
          {ceo.bio && (
            <p className="mt-4 text-gray-700 leading-7 max-w-3xl  md:w-[90%] ">
              {ceo.bio}
            </p>
          )}
          {/* <div className="mt-6 h-px w-full bg-gradient-to-r from-black/10 to-transparent" /> */}
          {/* Executives row */}
        </div>
      </div>
      <ul className="mt-6 pt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 border-t border-black/10">
        {executives.map((p) => (
          <li
            key={p.id}
            className="flex flex-col gap-4 rounded-2xl p-3 transition ">
            <div className="relative w-[200px] h-[200px] shrink-0 overflow-hidden rounded-xl ring-1 ring-black/10 group">
              <Image
                src={`${ASSET_PREFIX}${p.photo}`}
                alt={p.name}
                fill
                className="object-cover group-hover:scale-[1.15] transition duration-300"
                sizes="350px"
              />
            </div>
            <div>
              <p className="text-[18px]/[26px] md:text-[18px]/[26px] text-[#000] font-[500]   tracking-[-0.03em]">
                {p.name}
              </p>
              <p className="text-[17px]/[23px] md:text-[16px]/[24px] text-[#6b5622] font-[400]  tracking-[-0.03em]">
                {p.role}
              </p>
              {p.bio && (
                <p className="mt-4   text-[16px] leading-[22px] text-gray-700  md:w-[90%]">
                  {p.bio}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
      {/* Functional heads (optional) */}
      {functionalHeads.length > 0 && (
        <div className="pt-6 border-t border-black/10">
          <h4 className="text-[28px]/[36px] md:text-[32px]/[40px] lg:text-[36px]/[44px] xl:text-[40px]/[48px] 2xl:text-[48px]/[54px] text-[#000] font-[500] mb-[30px] md:mb-[50px] text-center tracking-[-0.03em] tracking-[-0.03em]">
            Functional Heads
          </h4>
          <div className="">
            <p className="text-[#000]/90 md:w-[80%] lg:w-[70%] mb-[20px] text-center mx-auto">
              Meet our seasoned team of functional heads, each an expert in their
              respective domains of sales, marketing, customer relations,
              purchase, planning, technology and branding.
            </p>
            <p className="text-[#000]/90 md:w-[80%] lg:w-[70%] text-center mx-auto">
              Together, they drive our real estate business forward, ensuring
              seamless transactions, innovative strategies and unparalleled
              customer satisfaction.
            </p>
            <ul className="flex flex-wrap gap-6 mt-[40px] justify-center">
              {functionalHeads.map((p) => (
                <li
                  key={p.id}
                  className="group bg-white transition  w-[200px]  xl:mx-[15px] 2xl:mx-[20px] overflow-hidden">
                  <div className="relative w-[200px] h-[200px] shrink-0 overflow-hidden rounded-xl ring-1 ring-black/10">
                    <Image
                      src={`${ASSET_PREFIX}${p.photo}`}
                      alt={p.name}
                      fill
                      className="object-cover object-top group-hover:scale-[1.15] transition duration-300"
                    />
                  </div>
                  <div className="p-3">
                    <p className="text-[18px]/[26px] md:text-[18px]/[26px] text-[#000] font-[500]  tracking-[-0.03em]">
                      {p.name}
                    </p>
                    <p className="text-[16px]/[24px] md:text-[16px]/[24px] text-[#6b5622] font-[400]    tracking-[-0.03em]">
                      {p.role}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------------------- VARIANT 2 ------------------------------ */
export function CardGrid({
  ceo,
  executives,
  functionalHeads = [],
}: LeadershipProps) {
  const everyone = [ceo, ...executives, ...functionalHeads];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {everyone.map((p) => (
        <article
          key={p.id}
          className="group relative overflow-hidden rounded-3xl border bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
          <div className="relative aspect-[4/5]">
            <Image
              src={p.photo}
              alt={p.name}
              fill
              className="object-cover group-hover:scale-[1.15] transition duration-300"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-90" />
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <h3 className="text-white font-semibold drop-shadow-sm">
                {p.name}
              </h3>
              <p className=" text-[16px] leading-[22px] text-[#6b5622]">{p.role}</p>
            </div>
          </div>
          {p.bio && <p className="p-4 text-[16px] leading-[22px] text-gray-700">{p.bio}</p>}
        </article>
      ))}
    </div>
  );
}

/* ----------------------------- SAMPLE DATA ---------------------------- */
// You can delete this and feed your own data from CMS/JSON.
export const sampleCeo: Person = {
  id: "ceo",
  name: "Mohamed Shafi",
  role: "Managing Partner / CEO",
  photo: "/images/mohamed-shafi-profile.webp",
  bio: "With a remarkable 24-year legacy at Real Estate, Shafi now spearheads a new chapter in our journey. His wealth of experience positions him as a guiding force as we venture into Unitd. His strategic vision ensures our commitment to innovation and excellence continues to flourish in this evolving real estate landscape.",
  accent: "#0B1220",
};

export const sampleExecs: Person[] = [
  {
    id: "biju",
    name: "Biju Chacko",
    role: "CFO",
    photo: "/images/biju-chacko.webp",
    bio: "Biju, an accomplished and results-driven finance professional with a keen focus on numbers, boasts 28 years of progressive experience in operations and production management across MNCs and startups. With a track record of driving numerical success, he has dedicated 16 years to making a substantial impact in the real estate domain",
  },
  {
    id: "arun",
    name: "Arun Prasad",
    role: "Head - Operations",
    photo: "/images/arunprasadh.webp",
    bio: `A dedicated "Task Master" with an engineering mastermind, Arun has been leading Operations and Projects in the construction industry for over 14 years. His expertise lies in site management, liaising, cost control, quality assurance, and technical and resources management.`,
  },
  {
    id: "anand",
    name: "Anand Sugun Babu",
    role: "Head - Org. Strategy & Growth",
    photo: "/images/anand.webp",
    bio: "A Human Capital Strategist with a wealth of two decades of cross-domain experience, adept at enhancing business capabilities by strategically and systematically unlocking the full potential of individuals. Specializing in Change Management, this professional has collaborated with major Multinationals in the realms of IT, Manufacturing, BPO, and Real Estate.",
  },
];

export const sampleHeads: Person[] = [

  {
    id: "jansi",
    name: "Jansi Samuel",
    role: "Head - Sales",
    photo: "/images/jansi-samuel.webp",
  },
  {
    id: "padmanabhan",
    name: "Padmanabhan B",
    role: "Head - Marketing",
    photo: "/images/padhu.webp",
  },
  {
    id: "mahesh",
    name: "Mahesh Kumar",
    role: "Senior Customer Delight Specialist",
    photo: "/images/mahesh.webp",
  },
  {
    id: "abhishek",
    name: "Abhishek ",
    role: "Head - Product Development",
    photo: "/images/abhishek.webp",
  },
  {
    id: "rameshkumar-r",
    name: "Rameshkumar R",
    role: "Chief Architect",
    photo: "/images/rameshkumar-r.webp",
  },
  {
    id: "rajeevan",
    name: "Rajeevan",
    role: "MRR Specialist",
    photo: "/images/rajeevan.webp",
  },
  {
    id: "rameshkumar-v",
    name: "Rameshkumar V",
    role: "Senior Engineer - Projects",
    photo: "/images/ramesh-kumar-v.webp",
  },

  // {
  //   id: "guna",
  //   name: "Guna",
  //   role: "Product Development",
  //   photo: "/images/guna.webp",
  // },
  // {
  //   id: "suresh",
  //   name: "Sureshkumar",
  //   role: "IT & Business Automation",
  //   photo: "/images/sureshkumar.webp",
  // },
];

/* ------------------------------- DEMO --------------------------------- */
export function DemoLeadership() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-10">
      <LeadershipSection
        ceo={sampleCeo}
        executives={sampleExecs}
        functionalHeads={sampleHeads}
        variant="split-hero"
      />

      <div className="my-12 h-px w-full bg-gradient-to-r from-transparent via-black/10 to-transparent" />

      <LeadershipSection
        ceo={sampleCeo}
        executives={sampleExecs}
        functionalHeads={sampleHeads}
        variant="card-grid"
      />
    </div>
  );
}
